import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Search, X, Trash2, Inbox, Loader2, Download,
  CheckSquare, Square, CreditCard, Send, FileText, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';

// ─── Constantes ───────────────────────────────────────────────────────────────

const STATUTS = [
  { key: 'en_cours', label: 'En cours', bg: '#FFF7ED', color: '#B45309', border: '#FCD34D', dot: '#F59E0B' },
  { key: 'paye',     label: 'Payé',     bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0', dot: '#22C55E' },
];

const STATUTS_REMISE = [
  { key: 'en_cours', label: 'En cours', bg: '#FFF7ED', color: '#B45309', border: '#FCD34D', dot: '#F59E0B' },
  { key: 'versee',   label: 'Versée',   bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE', dot: '#3B82F6' },
];

function getStatut(key)       { return STATUTS.find(s => s.key === key)       || STATUTS[0]; }
function getStatutRemise(key) { return STATUTS_REMISE.find(s => s.key === key) || STATUTS_REMISE[0]; }

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
}
function fmtMontant(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}
function fmtIban(iban) {
  if (!iban) return '';
  return iban.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
}

// ─── Badges statut ────────────────────────────────────────────────────────────

function StatutBadge({ statut }) {
  const s = getStatut(statut);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

function StatutBadgeRemise({ statut }) {
  const s = getStatutRemise(statut);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

// ─── Combobox fournisseur ──────────────────────────────────────────────────────

function FournisseurCombobox({ value, label, onChange }) {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    api.getEntreprises({ search: query || '', per_page: 30, exclude_gele: true })
      .then(r => { if (active) setOptions(r.data?.items || r.data || []); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, query]);

  const inpSt = {
    border: '1px solid var(--border)', borderRadius: 7, padding: '6px 10px',
    fontSize: 13, color: 'var(--text)', background: 'var(--surface)', outline: 'none', width: '100%',
  };

  return (
    <div className="relative" ref={ref}>
      <div
        onClick={() => setOpen(v => !v)}
        data-testid="fourn-combobox"
        style={{
          ...inpSt, cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 6,
        }}>
        <span style={{ color: label ? 'var(--text)' : 'var(--text-muted)' }}>
          {label || 'Sélectionner un fournisseur…'}
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>
      {open && (
        <div style={{
          position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'white', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
        }}>
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Rechercher…"
            style={{ width: '100%', padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--border-light)', fontSize: 13, outline: 'none' }}
          />
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {loading
              ? <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>Chargement…</div>
              : options.length === 0
                ? <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>Aucun résultat</div>
                : options.map(e => (
                  <div key={e.entreprise_id}
                    onClick={() => { onChange(e); setOpen(false); setQuery(''); }}
                    style={{
                      padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                      borderBottom: '1px solid var(--border-light)',
                    }}
                    onMouseEnter={ev => ev.currentTarget.style.background = 'var(--bg-muted,#f9fafb)'}
                    onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                    {e.nom}
                  </div>
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Modal création acompte individuel ────────────────────────────────────────

function ModalCreation({ onClose, onCreated, ibanEmetteur, nomEmetteur }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    fournisseur_id: '', fournisseur_nom: '', montant_ttc: '',
    iban_beneficiaire: '', iban_emetteur: ibanEmetteur || '',
    nom_emetteur: nomEmetteur || '', date_virement: today,
    libelle_virement: '', achat_ids: [],
  });
  const [achats, setAchats]               = useState([]);
  const [loadingAchats, setLoadingAchats] = useState(false);
  const [selectedAchats, setSelectedAchats] = useState(new Set());
  const [saving, setSaving]               = useState(false);

  useEffect(() => {
    setForm(f => ({ ...f, iban_emetteur: ibanEmetteur || '', nom_emetteur: nomEmetteur || '' }));
  }, [ibanEmetteur, nomEmetteur]);

  const handleFournisseurChange = useCallback(async (ent) => {
    const firstIban = ent.ibans?.[0]?.iban || '';
    setForm(f => ({ ...f, fournisseur_id: ent.entreprise_id, fournisseur_nom: ent.nom, iban_beneficiaire: firstIban, achat_ids: [] }));
    setSelectedAchats(new Set());
    setLoadingAchats(true);
    try {
      const r = await api.getAchats({
        fournisseur_id: ent.entreprise_id,
        stade: ['brouillon','devis_envoye','commande','en_cours_reception','receptionne'].join(','),
        per_page: 200,
      });
      setAchats(r.data || []);
    } catch { setAchats([]); }
    finally { setLoadingAchats(false); }
  }, []);

  const toggleAchat = (achat) => {
    setSelectedAchats(prev => {
      const next = new Set(prev);
      next.has(achat.achat_id) ? next.delete(achat.achat_id) : next.add(achat.achat_id);
      return next;
    });
  };

  useEffect(() => {
    const sel = achats.filter(a => selectedAchats.has(a.achat_id));
    const total = sel.reduce((s, a) => s + parseFloat(a.total_ttc || 0), 0);
    const numeros = sel.map(a => a.numero).join(', ');
    setForm(f => ({
      ...f,
      montant_ttc: total > 0 ? total.toFixed(2) : f.montant_ttc,
      libelle_virement: numeros ? `Acompte ${numeros}` : f.libelle_virement,
      achat_ids: sel.map(a => ({ achat_id: a.achat_id, numero: a.numero })),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAchats]);

  const handleSubmit = async () => {
    if (!form.fournisseur_id) { toast.error('Sélectionnez un fournisseur'); return; }
    if (!form.montant_ttc || parseFloat(form.montant_ttc) <= 0) { toast.error('Montant invalide'); return; }
    setSaving(true);
    try {
      const r = await api.createAcompte({ ...form, montant_ttc: parseFloat(form.montant_ttc) });
      toast.success(`Acompte ${r.data.numero} créé`);
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    } finally { setSaving(false); }
  };

  const inpSt = {
    border: '1px solid var(--border)', borderRadius: 7, padding: '6px 10px',
    fontSize: 13, color: 'var(--text)', background: 'var(--surface)', outline: 'none', width: '100%',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', padding: '28px 28px 24px' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>Nouveau virement / Acompte</h2>
          <button onClick={onClose} style={{ padding: 4, borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Fournisseur <span style={{ color: '#DC2626' }}>*</span></label>
            <FournisseurCombobox value={form.fournisseur_id} label={form.fournisseur_nom} onChange={handleFournisseurChange} />
          </div>

          {form.fournisseur_id && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Commandes d'achat associées</label>
              {loadingAchats
                ? <div style={{ padding: '8px 0', fontSize: 12, color: 'var(--text-muted)' }}><Loader2 size={13} className="inline mr-1 animate-spin" />Chargement…</div>
                : achats.length === 0
                  ? <div style={{ padding: '8px 0', fontSize: 12, color: 'var(--text-muted)' }}>Aucune commande en cours</div>
                  : (
                    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                      {achats.map((a, i) => (
                        <div key={a.achat_id} onClick={() => toggleAchat(a)} data-testid={`achat-row-${a.achat_id}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', background: selectedAchats.has(a.achat_id) ? '#EFF6FF' : 'white', borderTop: i > 0 ? '1px solid var(--border-light)' : 'none' }}>
                          <span style={{ color: selectedAchats.has(a.achat_id) ? 'var(--primary-solid)' : 'var(--text-muted)', flexShrink: 0 }}>
                            {selectedAchats.has(a.achat_id) ? <CheckSquare size={15} /> : <Square size={15} />}
                          </span>
                          <span style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>{a.numero}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDate(a.date_commande)}</span>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{fmtMontant(a.total_ttc)}</span>
                        </div>
                      ))}
                    </div>
                  )
              }
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Montant TTC (€) <span style={{ color: '#DC2626' }}>*</span></label>
              <input type="number" step="0.01" min="0" value={form.montant_ttc}
                onChange={e => setForm(f => ({ ...f, montant_ttc: e.target.value }))}
                style={inpSt} data-testid="montant-ttc-input" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Date de virement</label>
              <input type="date" value={form.date_virement}
                onChange={e => setForm(f => ({ ...f, date_virement: e.target.value }))}
                style={inpSt} data-testid="date-virement-input" />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Libellé du virement</label>
            <input type="text" value={form.libelle_virement}
              onChange={e => setForm(f => ({ ...f, libelle_virement: e.target.value }))}
              placeholder="Ex: Acompte BC-2026-0001" style={inpSt} maxLength={140}
              data-testid="libelle-virement-input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>IBAN bénéficiaire</label>
              <div style={{ ...inpSt, fontFamily: 'monospace', fontSize: 11, background: 'var(--bg-muted,#f9fafb)', color: form.iban_beneficiaire ? 'var(--text)' : 'var(--text-placeholder,#9CA3AF)', display: 'flex', alignItems: 'center' }}
                data-testid="iban-beneficiaire-display">
                {form.iban_beneficiaire ? fmtIban(form.iban_beneficiaire) : 'Non configuré'}
              </div>
              {!form.iban_beneficiaire && form.fournisseur_id && (
                <p style={{ fontSize: 11, color: '#DC2626', marginTop: 3 }}>Configurez l'IBAN dans la fiche du fournisseur</p>
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>IBAN émetteur</label>
              <div style={{ ...inpSt, fontFamily: 'monospace', fontSize: 11, background: 'var(--bg-muted,#f9fafb)', color: form.iban_emetteur ? 'var(--text-muted)' : 'var(--text-placeholder,#9CA3AF)', display: 'flex', alignItems: 'center' }}>
                {form.iban_emetteur ? fmtIban(form.iban_emetteur) : 'Non configuré'}
              </div>
              {form.nom_emetteur && <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{form.nom_emetteur}</p>}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6 pt-4" style={{ borderTop: '1px solid var(--border-light)' }}>
          <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'white', cursor: 'pointer' }}>Annuler</button>
          <button onClick={handleSubmit} disabled={saving} data-testid="save-acompte-btn"
            style={{ padding: '7px 20px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, color: 'white', background: 'var(--primary-gradient)', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />}
            {saving ? 'Enregistrement…' : "Créer l'acompte"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal édition acompte ────────────────────────────────────────────────────

function ModalEditAcompte({ acompte, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    montant_ttc:       acompte.montant_ttc ?? '',
    date_virement:     acompte.date_virement ? acompte.date_virement.slice(0, 10) : '',
    libelle_virement:  acompte.libelle_virement || '',
    iban_beneficiaire: acompte.iban_beneficiaire || '',
    iban_emetteur:     acompte.iban_emetteur || '',
    statut:            acompte.statut || 'en_cours',
  });
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.updateAcompte(acompte.acompte_id, {
        ...form,
        montant_ttc: parseFloat(form.montant_ttc),
      });
      toast.success('Virement mis à jour');
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur mise à jour');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer le virement ${acompte.numero} ?`)) return;
    setDeleting(true);
    try {
      await api.deleteAcompte(acompte.acompte_id);
      toast.success('Virement supprimé');
      onDeleted(acompte.acompte_id);
    } catch { toast.error('Erreur suppression'); }
    finally { setDeleting(false); }
  };

  const achatsRef = Array.isArray(acompte.achat_ids) ? acompte.achat_ids : [];
  const isReadOnly = acompte.statut === 'paye';

  const inpSt = {
    border: '1px solid var(--border)', borderRadius: 7, padding: '6px 10px',
    fontSize: 13, color: 'var(--text)', background: 'var(--surface)', outline: 'none', width: '100%',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', width: '100%', maxWidth: 540, maxHeight: '88vh', overflowY: 'auto', padding: '26px 26px 22px' }}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Virement</p>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
              {acompte.numero}
              <StatutBadge statut={acompte.statut} />
            </h2>
          </div>
          <button onClick={onClose} style={{ padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={17} /></button>
        </div>

        <div className="space-y-4">
          <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--bg-muted,#f9fafb)', border: '1px solid var(--border)' }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>Fournisseur</p>
            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{acompte.fournisseur_nom || '—'}</p>
            {achatsRef.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                {achatsRef.map(ar => (
                  <span key={ar.achat_id} style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>{ar.numero}</span>
                ))}
              </div>
            )}
            {acompte.remise_id && (
              <div style={{ marginTop: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                  Remise : {acompte.remise_numero || acompte.remise_id}
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Montant TTC (€)</label>
              <input type="number" step="0.01" min="0" value={form.montant_ttc}
                onChange={e => upd('montant_ttc', e.target.value)} disabled={isReadOnly}
                style={{ ...inpSt, background: isReadOnly ? 'var(--bg-muted,#f9fafb)' : 'var(--surface)' }}
                data-testid="edit-montant" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Date de virement</label>
              <input type="date" value={form.date_virement}
                onChange={e => upd('date_virement', e.target.value)} disabled={isReadOnly}
                style={{ ...inpSt, background: isReadOnly ? 'var(--bg-muted,#f9fafb)' : 'var(--surface)' }}
                data-testid="edit-date" />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Libellé</label>
            <input type="text" value={form.libelle_virement}
              onChange={e => upd('libelle_virement', e.target.value)} disabled={isReadOnly}
              maxLength={140}
              style={{ ...inpSt, background: isReadOnly ? 'var(--bg-muted,#f9fafb)' : 'var(--surface)' }}
              data-testid="edit-libelle" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>IBAN bénéficiaire</label>
              <div style={{ ...inpSt, fontFamily: 'monospace', fontSize: 11, background: 'var(--bg-muted,#f9fafb)', color: form.iban_beneficiaire ? 'var(--text)' : 'var(--text-placeholder,#9CA3AF)' }} data-testid="edit-iban-benef">
                {form.iban_beneficiaire ? fmtIban(form.iban_beneficiaire) : 'Non configuré'}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>IBAN émetteur</label>
              <div style={{ ...inpSt, fontFamily: 'monospace', fontSize: 11, background: 'var(--bg-muted,#f9fafb)', color: form.iban_emetteur ? 'var(--text-muted)' : 'var(--text-placeholder,#9CA3AF)' }} data-testid="edit-iban-emet">
                {form.iban_emetteur ? fmtIban(form.iban_emetteur) : 'Non configuré'}
              </div>
            </div>
          </div>

          {!isReadOnly && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Statut</label>
              <select value={form.statut} onChange={e => upd('statut', e.target.value)}
                style={{ ...inpSt, cursor: 'pointer' }} data-testid="edit-statut">
                <option value="en_cours">En cours</option>
                <option value="paye">Payé</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-6 pt-4" style={{ borderTop: '1px solid var(--border-light)' }}>
          {!isReadOnly ? (
            <button onClick={handleDelete} disabled={deleting} data-testid="edit-delete-btn"
              style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #FECACA', fontSize: 13, fontWeight: 600, color: '#DC2626', background: '#FEF2F2', cursor: deleting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
              {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Supprimer
            </button>
          ) : <div />}
          <div className="flex gap-3">
            <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'white', cursor: 'pointer' }}>Fermer</button>
            {!isReadOnly && (
              <button onClick={handleSave} disabled={saving} data-testid="edit-save-btn"
                style={{ padding: '7px 20px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, color: 'white', background: saving ? '#9CA3AF' : 'var(--primary-gradient)', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : null}
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modal création remise ─────────────────────────────────────────────────────

function ModalCreationRemise({ selectedAcomptes, ibanEmetteur, nomEmetteur, onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [dateRemise, setDateRemise] = useState(today);
  const [saving, setSaving] = useState(false);

  const totalMontant = selectedAcomptes.reduce((s, a) => s + parseFloat(a.montant_ttc || 0), 0);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const r = await api.createRemise({
        acompte_ids: selectedAcomptes.map(a => a.acompte_id),
        date_remise: dateRemise,
      });
      toast.success(`Remise ${r.data.numero} créée — ${selectedAcomptes.length} virement(s) regroupé(s)`);
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création remise');
    } finally { setSaving(false); }
  };

  const inpSt = {
    border: '1px solid var(--border)', borderRadius: 7, padding: '6px 10px',
    fontSize: 13, color: 'var(--text)', background: 'var(--surface)', outline: 'none', width: '100%',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', width: '100%', maxWidth: 560, maxHeight: '88vh', overflowY: 'auto', padding: '26px 26px 22px' }}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Nouvelle remise</p>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Regrouper en remise SEPA</h2>
          </div>
          <button onClick={onClose} style={{ padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={17} /></button>
        </div>

        <div className="space-y-4">
          {/* Date */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Date de remise</label>
            <input type="date" value={dateRemise} onChange={e => setDateRemise(e.target.value)}
              style={{ ...inpSt, maxWidth: 200 }} data-testid="remise-date-input" />
          </div>

          {/* IBAN émetteur */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>IBAN émetteur</label>
            <div style={{ ...inpSt, fontFamily: 'monospace', fontSize: 11, background: 'var(--bg-muted,#f9fafb)', color: ibanEmetteur ? 'var(--text-muted)' : '#DC2626', display: 'flex', alignItems: 'center' }}>
              {ibanEmetteur ? fmtIban(ibanEmetteur) : 'Non configuré — vérifiez la fiche société'}
            </div>
            {nomEmetteur && <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{nomEmetteur}</p>}
          </div>

          {/* Récap virements */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              Virements inclus ({selectedAcomptes.length})
            </label>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {selectedAcomptes.map((a, i) => (
                <div key={a.acompte_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: i > 0 ? '1px solid var(--border-light)' : 'none', background: 'white' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', flexShrink: 0 }}>{a.numero}</span>
                  <span style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.fournisseur_nom}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', flexShrink: 0 }}>{fmtMontant(a.montant_ttc)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 8, background: 'var(--bg-muted,#f9fafb)', border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>Total remise</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>{fmtMontant(totalMontant)}</span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6 pt-4" style={{ borderTop: '1px solid var(--border-light)' }}>
          <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'white', cursor: 'pointer' }}>Annuler</button>
          <button onClick={handleSubmit} disabled={saving} data-testid="create-remise-btn"
            style={{ padding: '7px 20px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, color: 'white', background: saving ? '#9CA3AF' : 'var(--primary-gradient)', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Layers size={13} />}
            {saving ? 'Création…' : 'Créer la remise'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal détail remise ───────────────────────────────────────────────────────

function ModalDetailRemise({ remise: remiseInit, onClose, onVersed, onDeleted }) {
  const [remise, setRemise]       = useState(remiseInit);
  const [acomptes, setAcomptes]   = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [versing, setVersing]     = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    api.getRemise(remise.remise_id)
      .then(r => {
        if (!active) return;
        setRemise(r.data);
        setAcomptes(r.data.acomptes || []);
      })
      .catch(() => { if (active) setAcomptes([]); })
      .finally(() => { if (active) setLoadingDetail(false); });
    return () => { active = false; };
  }, [remise.remise_id]);

  const handleDownloadXml = async () => {
    setDownloading(true);
    try {
      const r = await api.downloadRemiseXml(remise.remise_id);
      const blob = new Blob([r.data], { type: 'application/xml' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `${remise.numero}_SEPA.xml`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Fichier XML téléchargé');
    } catch { toast.error('Erreur téléchargement XML'); }
    finally { setDownloading(false); }
  };

  const handleVerser = async () => {
    const nb = (acomptes || []).length;
    if (!window.confirm(`Générer le XML et verser la remise ${remise.numero} ?\nLes ${nb} virement(s) inclus seront marqués Payé.`)) return;
    setVersing(true);
    try {
      const r = await api.verserRemise(remise.remise_id);
      toast.success(`Remise ${remise.numero} versée — ${nb} virement(s) marqué(s) Payé`);
      setRemise(r.data);
      setAcomptes(prev => (prev || []).map(a => ({ ...a, statut: 'paye' })));
      onVersed(r.data);
      // Déclenche le téléchargement
      setTimeout(() => handleDownloadXml(), 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur versement');
    } finally { setVersing(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer la remise ${remise.numero} ?\nLes virements liés seront déliés (ils repasseront En cours).`)) return;
    setDeleting(true);
    try {
      await api.deleteRemise(remise.remise_id);
      toast.success('Remise supprimée');
      onDeleted(remise.remise_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    } finally { setDeleting(false); }
  };

  const isVersee = remise.statut === 'versee';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', width: '100%', maxWidth: 1160, maxHeight: '88vh', overflowY: 'auto', padding: '26px 26px 22px' }}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Remise de virement</p>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
              {remise.numero}
              <StatutBadgeRemise statut={remise.statut} />
            </h2>
          </div>
          <button onClick={onClose} style={{ padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={17} /></button>
        </div>

        {/* Infos remise */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-muted,#f9fafb)', border: '1px solid var(--border)' }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>DATE</p>
            <p style={{ fontSize: 13, fontWeight: 700 }}>{fmtDate(remise.date_remise)}</p>
          </div>
          <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-muted,#f9fafb)', border: '1px solid var(--border)' }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>TOTAL</p>
            <p style={{ fontSize: 13, fontWeight: 700 }}>{fmtMontant(remise.montant_total)}</p>
          </div>
          <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-muted,#f9fafb)', border: '1px solid var(--border)' }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>VIREMENTS</p>
            <p style={{ fontSize: 13, fontWeight: 700 }}>{loadingDetail ? '…' : (acomptes || []).length}</p>
          </div>
        </div>

        {remise.iban_emetteur && (
          <div style={{ marginBottom: 16, padding: '8px 12px', borderRadius: 8, background: 'var(--bg-muted,#f9fafb)', border: '1px solid var(--border)', fontSize: 12 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>IBAN émetteur : </span>
            <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{fmtIban(remise.iban_emetteur)}</span>
            {remise.nom_emetteur && <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>({remise.nom_emetteur})</span>}
          </div>
        )}

        {/* Liste des virements */}
        <div>
          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>VIREMENTS INCLUS</p>
          {loadingDetail ? (
            <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--primary-solid)' }} /></div>
          ) : (acomptes || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 13, color: 'var(--text-muted)' }}>Aucun virement</div>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'var(--bg-muted,#f9fafb)', borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>N°</th>
                    <th style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Fournisseur</th>
                    <th style={{ padding: '7px 10px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Montant</th>
                    <th style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Date</th>
                    <th style={{ padding: '7px 10px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {(acomptes || []).map((a, i) => (
                    <tr key={a.acompte_id} style={{ borderTop: i > 0 ? '1px solid var(--border-light)' : 'none' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 700, fontSize: 12 }}>{a.numero}</td>
                      <td style={{ padding: '8px 10px', fontSize: 12 }}>{a.fournisseur_nom}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: 12 }}>{fmtMontant(a.montant_ttc)}</td>
                      <td style={{ padding: '8px 10px', fontSize: 11, color: 'var(--text-secondary)' }}>{fmtDate(a.date_virement)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}><StatutBadge statut={a.statut} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mt-6 pt-4" style={{ borderTop: '1px solid var(--border-light)' }}>
          <div>
            {!isVersee && (
              <button onClick={handleDelete} disabled={deleting} data-testid="delete-remise-btn"
                style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #FECACA', fontSize: 13, fontWeight: 600, color: '#DC2626', background: '#FEF2F2', cursor: deleting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Supprimer la remise
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'white', cursor: 'pointer' }}>Fermer</button>
            {isVersee ? (
              <button onClick={handleDownloadXml} disabled={downloading} data-testid="download-xml-btn"
                style={{ padding: '7px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, color: 'white', background: downloading ? '#9CA3AF' : '#1D4ED8', cursor: downloading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                {downloading ? 'Téléchargement…' : 'Regénérer le XML'}
              </button>
            ) : (
              <button onClick={handleVerser} disabled={versing || loadingDetail} data-testid="verser-remise-btn"
                style={{ padding: '7px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, color: 'white', background: (versing || loadingDetail) ? '#9CA3AF' : 'var(--primary-gradient)', cursor: (versing || loadingDetail) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                {versing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                {versing ? 'Versement…' : 'Générer le XML et verser'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────

export default function Acomptes() {
  const { activeSociete, activeAgences } = useSociete();

  // Onglets
  const [activeTab, setActiveTab]     = useState('paiements');

  // Paiements
  const [acomptes, setAcomptes]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [filterStatut, setFilterStatut] = useState('');
  const [search, setSearch]           = useState('');
  const [selected, setSelected]       = useState(new Set());
  const [showModal, setShowModal]     = useState(false);
  const [editAcompte, setEditAcompte] = useState(null);
  const [showModalRemise, setShowModalRemise] = useState(false);

  // Remises
  const [remises, setRemises]         = useState([]);
  const [loadingRemises, setLoadingRemises] = useState(false);
  const [detailRemise, setDetailRemise] = useState(null);
  const [filterStatutRemise, setFilterStatutRemise] = useState('');

  // Contexte IBAN émetteur
  const [ibanEmetteur, setIbanEmetteur] = useState('');
  const [nomEmetteur, setNomEmetteur]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatut) params.statut = filterStatut;
      if (search.trim()) params.q = search.trim();
      const r = await api.getAcomptes(params);
      setAcomptes(r.data || []);
    } catch {
      toast.error('Erreur chargement des acomptes');
    } finally { setLoading(false); }
  }, [filterStatut, search]);

  const loadRemises = useCallback(async () => {
    setLoadingRemises(true);
    try {
      const params = {};
      if (filterStatutRemise) params.statut = filterStatutRemise;
      const r = await api.getRemises(params);
      setRemises(r.data || []);
    } catch {
      toast.error('Erreur chargement des remises');
    } finally { setLoadingRemises(false); }
  }, [filterStatutRemise]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRemises(); }, [loadRemises]);

  useEffect(() => {
    api.getAcompteIbanEmetteur()
      .then(r => { setIbanEmetteur(r.data?.iban || ''); setNomEmetteur(r.data?.nom || ''); })
      .catch(() => {});
  }, [activeSociete, activeAgences]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const eligible = acomptes.filter(a => a.statut === 'en_cours' && !a.remise_id);
    if (selected.size === eligible.length && eligible.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(eligible.map(a => a.acompte_id)));
    }
  };

  const handleDelete = async (acompte) => {
    if (!window.confirm(`Supprimer l'acompte ${acompte.numero} ?`)) return;
    try {
      await api.deleteAcompte(acompte.acompte_id);
      setAcomptes(prev => prev.filter(a => a.acompte_id !== acompte.acompte_id));
      setSelected(prev => { const n = new Set(prev); n.delete(acompte.acompte_id); return n; });
      toast.success('Acompte supprimé');
    } catch { toast.error('Erreur suppression'); }
  };

  // Acomptes éligibles à une remise (en_cours, pas déjà dans une remise)
  const eligibleSelected = acomptes.filter(a =>
    selected.has(a.acompte_id) && a.statut === 'en_cours' && !a.remise_id
  );

  const allEligible = acomptes.filter(a => a.statut === 'en_cours' && !a.remise_id);
  const allEligibleSelected = allEligible.length > 0 && selected.size === allEligible.length;
  const someSelected = selected.size > 0 && !allEligibleSelected;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px 28px', minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)' }}>
      {/* En-tête */}
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <CreditCard size={22} style={{ color: 'var(--primary-solid)' }} />
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Acomptes / Virements</h1>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="new-acompte-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', background: 'var(--primary-gradient)', color: 'white', cursor: 'pointer' }}>
          <Plus size={14} />
          Nouveau virement
        </button>
      </div>

      {/* Onglets */}
      <div className="flex items-center gap-1 mb-5" style={{ borderBottom: '1px solid var(--border)' }}>
        {[
          { key: 'paiements', label: 'Paiements', icon: <CreditCard size={13} /> },
          { key: 'remises',   label: 'Remises',   icon: <Layers size={13} /> },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: '8px 8px 0 0', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: 'none', borderBottom: activeTab === tab.key ? '2px solid var(--primary-solid)' : '2px solid transparent',
              background: 'transparent',
              color: activeTab === tab.key ? 'var(--primary-solid)' : 'var(--text-muted)',
              marginBottom: -1,
            }}>
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Onglet Paiements ─────────────────────────────────────────────────── */}
      {activeTab === 'paiements' && (
        <>
          {/* Barre d'actions sélection */}
          {eligibleSelected.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 14, borderRadius: 8, background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1D4ED8' }}>{eligibleSelected.length} virement(s) sélectionné(s)</span>
              <button onClick={() => setShowModalRemise(true)} data-testid="btn-creer-remise"
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 7, border: 'none', fontSize: 12, fontWeight: 700, background: '#1D4ED8', color: 'white', cursor: 'pointer' }}>
                <Layers size={12} />
                Créer une remise
              </button>
              <button onClick={() => setSelected(new Set())}
                style={{ fontSize: 12, color: '#1D4ED8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                Désélectionner
              </button>
            </div>
          )}

          {/* Filtres */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative flex-shrink-0">
              <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…"
                style={{ paddingLeft: 30, paddingRight: 10, paddingTop: 7, paddingBottom: 7, border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, width: 200, background: 'var(--surface)', color: 'var(--text)', outline: 'none' }}
                data-testid="search-input" />
              {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={12} /></button>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setFilterStatut('')} data-testid="filter-all"
                style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${!filterStatut ? 'var(--primary-solid)' : 'var(--border)'}`, background: !filterStatut ? 'var(--primary-50)' : 'white', color: !filterStatut ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
                Tous
              </button>
              {STATUTS.map(s => (
                <button key={s.key} onClick={() => setFilterStatut(filterStatut === s.key ? '' : s.key)} data-testid={`filter-${s.key}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${filterStatut === s.key ? s.border : 'var(--border)'}`, background: filterStatut === s.key ? s.bg : 'white', color: filterStatut === s.key ? s.color : 'var(--text-muted)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tableau paiements */}
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin" style={{ color: 'var(--primary-solid)' }} /></div>
          ) : acomptes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Inbox size={36} style={{ color: 'var(--text-disabled)' }} />
              <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>Aucun virement</p>
              <button onClick={() => setShowModal(true)} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: 'var(--primary-gradient)', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
                Créer un premier virement
              </button>
            </div>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <table className="w-full text-sm" data-testid="acomptes-table">
                <thead>
                  <tr style={{ background: 'var(--bg-muted,#f9fafb)', borderBottom: '1px solid var(--border)' }}>
                    <th style={{ width: 36, padding: '9px 12px', textAlign: 'center' }}>
                      <button onClick={toggleSelectAll} style={{ background: 'none', border: 'none', cursor: 'pointer', color: (allEligibleSelected || someSelected) ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
                        {allEligibleSelected ? <CheckSquare size={14} /> : someSelected ? <CheckSquare size={14} style={{ opacity: 0.5 }} /> : <Square size={14} />}
                      </button>
                    </th>
                    {['Numéro','Fournisseur','Montant TTC','Date virement','Commandes','Remise','Statut',''].map((h, i) => (
                      <th key={i} style={{ padding: '9px 12px', textAlign: ['Montant TTC'].includes(h) ? 'right' : [''].includes(h) ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {acomptes.map((a, i) => {
                    const isSelected = selected.has(a.acompte_id);
                    const achatsRef  = Array.isArray(a.achat_ids) ? a.achat_ids : [];
                    const canSelect  = a.statut === 'en_cours' && !a.remise_id;
                    return (
                      <tr key={a.acompte_id} data-testid={`acompte-row-${a.acompte_id}`}
                        onClick={() => setEditAcompte(a)}
                        style={{ borderTop: i > 0 ? '1px solid var(--border-light)' : 'none', background: isSelected ? '#EFF6FF' : 'white', transition: 'background 0.1s', cursor: 'pointer' }}>
                        <td style={{ padding: '9px 12px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                          {canSelect && (
                            <button onClick={() => toggleSelect(a.acompte_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: isSelected ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
                              {isSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                            </button>
                          )}
                        </td>
                        <td style={{ padding: '9px 12px' }}><span style={{ fontWeight: 700, fontSize: 13 }}>{a.numero}</span></td>
                        <td style={{ padding: '9px 12px' }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{a.fournisseur_nom}</div>
                          {a.iban_beneficiaire && (
                            <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{fmtIban(a.iban_beneficiaire)}</div>
                          )}
                        </td>
                        <td style={{ padding: '9px 12px', textAlign: 'right' }}><span style={{ fontWeight: 700, fontSize: 13 }}>{fmtMontant(a.montant_ttc)}</span></td>
                        <td style={{ padding: '9px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>{fmtDate(a.date_virement)}</td>
                        <td style={{ padding: '9px 12px' }}>
                          {achatsRef.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {achatsRef.map(ar => (
                                <span key={ar.achat_id} style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>{ar.numero}</span>
                              ))}
                            </div>
                          ) : <span style={{ fontSize: 12, color: 'var(--text-disabled)' }}>—</span>}
                        </td>
                        <td style={{ padding: '9px 12px' }}>
                          {a.remise_numero ? (
                            <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 4, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', fontFamily: 'monospace' }}>{a.remise_numero}</span>
                          ) : <span style={{ fontSize: 12, color: 'var(--text-disabled)' }}>—</span>}
                        </td>
                        <td style={{ padding: '9px 12px' }}><StatutBadge statut={a.statut} /></td>
                        <td style={{ padding: '9px 12px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                          {a.statut === 'en_cours' && !a.remise_id && (
                            <button onClick={() => handleDelete(a)} data-testid={`delete-acompte-${a.acompte_id}`} title="Supprimer"
                              style={{ padding: 5, borderRadius: 6, border: '1px solid var(--border)', background: 'white', cursor: 'pointer' }}>
                              <Trash2 size={12} style={{ color: '#DC2626' }} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Onglet Remises ───────────────────────────────────────────────────── */}
      {activeTab === 'remises' && (
        <>
          {/* Filtres remises */}
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setFilterStatutRemise('')}
              style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${!filterStatutRemise ? 'var(--primary-solid)' : 'var(--border)'}`, background: !filterStatutRemise ? 'var(--primary-50)' : 'white', color: !filterStatutRemise ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
              Toutes
            </button>
            {STATUTS_REMISE.map(s => (
              <button key={s.key} onClick={() => setFilterStatutRemise(filterStatutRemise === s.key ? '' : s.key)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${filterStatutRemise === s.key ? s.border : 'var(--border)'}`, background: filterStatutRemise === s.key ? s.bg : 'white', color: filterStatutRemise === s.key ? s.color : 'var(--text-muted)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
                {s.label}
              </button>
            ))}
          </div>

          {/* Tableau remises */}
          {loadingRemises ? (
            <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin" style={{ color: 'var(--primary-solid)' }} /></div>
          ) : remises.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <FileText size={36} style={{ color: 'var(--text-disabled)' }} />
              <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>Aucune remise</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sélectionnez des virements dans l'onglet Paiements pour créer une remise</p>
            </div>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <table className="w-full text-sm" data-testid="remises-table">
                <thead>
                  <tr style={{ background: 'var(--bg-muted,#f9fafb)', borderBottom: '1px solid var(--border)' }}>
                    {['Numéro','Date','Montant total','Virements','IBAN émetteur','Statut','Actions'].map((h, i) => (
                      <th key={i} style={{ padding: '9px 12px', textAlign: h === 'Montant total' ? 'right' : h === 'Actions' ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {remises.map((r, i) => (
                    <tr key={r.remise_id} data-testid={`remise-row-${r.remise_id}`}
                      onClick={() => setDetailRemise(r)}
                      style={{ borderTop: i > 0 ? '1px solid var(--border-light)' : 'none', background: 'white', cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={ev => ev.currentTarget.style.background = 'var(--bg-muted,#f9fafb)'}
                      onMouseLeave={ev => ev.currentTarget.style.background = 'white'}>
                      <td style={{ padding: '9px 12px' }}><span style={{ fontWeight: 700, fontSize: 13 }}>{r.numero}</span></td>
                      <td style={{ padding: '9px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>{fmtDate(r.date_remise)}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'right' }}><span style={{ fontWeight: 700, fontSize: 13 }}>{fmtMontant(r.montant_total)}</span></td>
                      <td style={{ padding: '9px 12px' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: 'var(--bg-muted,#f9fafb)', border: '1px solid var(--border)' }}>{r.nb_acomptes} virement{r.nb_acomptes > 1 ? 's' : ''}</span>
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        {r.iban_emetteur
                          ? <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--text-muted)' }}>{fmtIban(r.iban_emetteur)}</span>
                          : <span style={{ fontSize: 12, color: 'var(--text-disabled)' }}>—</span>
                        }
                      </td>
                      <td style={{ padding: '9px 12px' }}><StatutBadgeRemise statut={r.statut} /></td>
                      <td style={{ padding: '9px 12px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-2">
                          {r.statut === 'versee' && (
                            <button onClick={() => {
                              setDetailRemise(r);
                              // Le téléchargement se fait depuis le modal
                            }}
                              title="Télécharger le XML"
                              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Download size={11} />
                              XML
                            </button>
                          )}
                          {r.statut === 'en_cours' && (
                            <button onClick={() => setDetailRemise(r)}
                              title="Ouvrir"
                              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'white', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Send size={11} />
                              Verser
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Modales */}
      {showModal && (
        <ModalCreation
          onClose={() => setShowModal(false)}
          onCreated={(acompte) => { setAcomptes(prev => [acompte, ...prev]); setShowModal(false); }}
          ibanEmetteur={ibanEmetteur}
          nomEmetteur={nomEmetteur}
        />
      )}

      {editAcompte && (
        <ModalEditAcompte
          acompte={editAcompte}
          onClose={() => setEditAcompte(null)}
          onSaved={(updated) => { setAcomptes(prev => prev.map(a => a.acompte_id === updated.acompte_id ? updated : a)); setEditAcompte(null); }}
          onDeleted={(id) => { setAcomptes(prev => prev.filter(a => a.acompte_id !== id)); setSelected(prev => { const n = new Set(prev); n.delete(id); return n; }); setEditAcompte(null); }}
        />
      )}

      {showModalRemise && eligibleSelected.length > 0 && (
        <ModalCreationRemise
          selectedAcomptes={eligibleSelected}
          ibanEmetteur={ibanEmetteur}
          nomEmetteur={nomEmetteur}
          onClose={() => setShowModalRemise(false)}
          onCreated={(remise) => {
            setShowModalRemise(false);
            setSelected(new Set());
            load(); // Recharge les acomptes (ils ont maintenant un remise_id)
            setRemises(prev => [{ ...remise }, ...prev]);
            setActiveTab('remises'); // Bascule vers l'onglet Remises
          }}
        />
      )}

      {detailRemise && (
        <ModalDetailRemise
          remise={detailRemise}
          onClose={() => setDetailRemise(null)}
          onVersed={(updated) => {
            setRemises(prev => prev.map(r => r.remise_id === updated.remise_id ? { ...r, ...updated } : r));
            load(); // Recharge les acomptes (statut paye)
          }}
          onDeleted={(id) => {
            setRemises(prev => prev.filter(r => r.remise_id !== id));
            setDetailRemise(null);
            load(); // Recharge les acomptes (remise_id NULL)
          }}
        />
      )}
    </div>
  );
}
